import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import {
    AuthState,
    PersonalAccount,
    User,
} from "../types/Store/AuthStore.types";
import authApiClient from "../api/api.auth";
import { enqueueSnackbar, useSnackbar } from "notistack";
import { useNavigate, useNavigation } from "react-router-dom";

function getBrowserFingerprint() {
    // Собираем только СТАБИЛЬНЫЕ данные браузера.
    // Намеренно НЕ используем:
    //  - screen.width/height напрямую (на iOS меняются местами при повороте экрана)
    //  - timezone (меняется при DST и в поездках)
    //  - navigator.platform (deprecated)
    // Размер экрана нормализуем по max/min, чтобы поворот не влиял на отпечаток,
    // но привязка к разрешению (модели устройства) сохранялась.
    const screenSize =
        Math.max(screen.width, screen.height) +
        'x' +
        Math.min(screen.width, screen.height);

    const data = [
        navigator.userAgent,
        navigator.language,
        screen.colorDepth + 'bit',
        screenSize,
    ].join('|');

    // encodeURIComponent на случай не-Latin1 символов (btoa иначе бросает)
    return btoa(encodeURIComponent(data));
}

// Один общий in-flight refresh на все клиенты — защита от параллельных refresh,
// которые ротация refresh-токена на бэке иначе превращает в разлогин.
let inFlightRefresh: Promise<{ accessToken: string | null }> | null = null;

export const useAuthStore = create<AuthState>()(
    persist(
        (set, get) => ({
            accessToken: null,
            refreshToken: null,
            user: null,
            accounts: null,
            activeAccount: null,
            isAuthenticated: false,
            fingerPrint: "",
            userAgent: "",
            isLoading: false,

            setUserBrowserData: (userAgent) => {
                // Синхронно: формула детерминированная, await не нужен.
                // Убирает гонку, когда refresh уходил до готовности fingerprint.
                const fp = getBrowserFingerprint();
                set({ userAgent, fingerPrint: fp });
            },

            login: async (LoginName: string, password: string) => {
                try {
                    const res = await authApiClient.post(
                        "/api/v1/auth/login",
                        {
                            login: LoginName.trim().split(" ").join(""),
                            password: password,
                            user_agent: get().userAgent || navigator.userAgent,
                            fingerprint: get().fingerPrint || getBrowserFingerprint(),
                        }
                    );
                    if (res.status === 200) {
                        set({
                            accessToken: res.data.access_token,
                            refreshToken: res.data.refresh_token,
                            isAuthenticated: true,
                        });
                        await get().fetchMe();
                        location.href = "/admin";
                    }
                } catch (error: any) {
                    throw error;
                }
            },

            logout: () => {
                set({
                    accessToken: null,
                    user: null,
                    isAuthenticated: false,
                    refreshToken: null,
                    accounts: null,
                    activeAccount: null,
                });
            },

            async fetchMe() {
                try {
                    const token = get().accessToken;
                    if (!token) {
                        set({
                            accessToken: null,
                            user: null,
                            refreshToken: null,
                            isAuthenticated: false,
                        });
                        return;
                    }

                    const res = await authApiClient.get(
                        "/api/v1/user/user-info",
                    );

                    if (res.status === 200) {
                        const user = res.data as User;
                        const { data }: { data: Array<PersonalAccount> } =
                            await authApiClient.get(
                                "/api/v1/user/personal-accounts"
                            );

                        set({
                            accounts: data,
                            activeAccount: get().activeAccount
                                ? get().activeAccount
                                : data[0].id,
                            user,
                        });
                    } else {
                        throw res;
                    }
                } catch (error: any) {
                    // 401 уже обработан интерсептором authApiClient (там идёт refresh
                    // и повторный запрос). Если ошибка всё же дошла сюда с 401/403 —
                    // значит сессия невалидна и refresh не помог: чистим стейт, чтобы
                    // AuthProvider увёл на логин, а не оставлял белый экран с протухшими
                    // токенами.
                    const status = error?.response?.status;
                    if (status === 401 || status === 403) {
                        get().logout();
                    } else {
                        console.error("fetchMe failed:", error);
                    }
                }
            },

            refresh: async () => {
                // Дедупликация одновременных refresh. getReadings и т.п. шлют пачку
                // параллельных запросов; при протухшем токене все они получают 401
                // и зовут refresh одновременно. Бэкенд РОТИРУЕТ refresh-токен (старый
                // удаляется), поэтому второй параллельный refresh упал бы с
                // SESSION_NOT_FOUND и разлогинил. Делим один in-flight запрос на всех.
                if (inFlightRefresh) {
                    return inFlightRefresh;
                }

                const { refreshToken } = get();
                if (!refreshToken) {
                    get().logout();
                    set({ isLoading: false });
                    return {accessToken: null};
                }

                inFlightRefresh = (async () => {
                    try {
                        get().setIsLoading(true);
                        // Подстраховка: если по какой-то причине setUserBrowserData ещё не
                        // успел записать fingerprint в стор — считаем его на месте.
                        // Формула детерминированная, значение совпадёт с тем, что было при логине.
                        const fingerPrint = get().fingerPrint || getBrowserFingerprint();
                        const res = await authApiClient.post(
                            "/api/v1/auth/refresh-tokens",
                            {
                                refresh_token: refreshToken,
                                user_agent: navigator.userAgent,
                                fingerprint: fingerPrint,
                            }
                        );
                        const accessToken = res.data.access_token;
                        const refreshTokenNew = res.data.refresh_token;

                        set({
                            accessToken,
                            refreshToken: refreshTokenNew,
                            isAuthenticated: true,
                        });

                        return {accessToken};
                    } catch (err) {
                        console.warn("Refresh failed:", err);
                        get().logout();
                        return {accessToken: null};
                    } finally {
                        set({ isLoading: false });
                        inFlightRefresh = null;
                    }
                })();

                return inFlightRefresh;
            },
            setIsLoading(isLoading: boolean) {
                set({ isLoading });
            },

            setActiveAccount(id: number) {
                set({ activeAccount: id });
            },

            registrationEmail: null,
            isAcceptedRegistration: false,
            hash: "",

            registration: async (hash: string) => {
                try {
                    const res = await authApiClient.get(
                        "/api/v1/auth/registration?hash=" + hash
                    );

                    console.log("REGISTRATION ", res);

                    if (res.status === 200) {
                        set({ registrationEmail: res.data.email, hash });
                        return res.data.email;
                    }
                } catch (error: any) {
                    if (error.response) {
                        enqueueSnackbar({
                            message:
                                error.response?.data?.detail ||
                                "Неизвестная серверная ошибка. Попробуйте заново",
                            variant: "error",
                        });
                        setTimeout(() => {
                            location.href = "/";
                        }, 2000);
                    }
                }
                return "";
            },
            sendCode: async (code: string, hash: string) => {
                try {
                    const res = await authApiClient.post(
                        "/api/v1/auth/check-code",
                        {
                            code,
                            hash,
                        }
                    );

                    if (res.status == 200) {
                        set({ isAcceptedRegistration: true });
                        return res.data.success;
                    } else {
                        return false;
                    }
                } catch (error: any) {
                    enqueueSnackbar({
                        message:
                            error.response?.data?.detail ||
                            "Неизвестная серверная ошибка. Попробуйте заново",
                        variant: "error",
                    });
                    return false;
                }
            },

            sendPassword: async (password: string) => {
                try {
                    const { data } = await authApiClient.post(
                        "/api/v1/auth/set-password",
                        {
                            hash: get().hash,
                            password,
                            user_agent: get().userAgent,
                            fingerprint: get().fingerPrint,
                        }
                    );

                    const { access_token, refresh_token } = data;

                    set({
                        accessToken: access_token,
                        refreshToken: refresh_token,
                    });

                    await get().fetchMe();

                    location.href = "/admin";
                } catch (error: any) {
                    enqueueSnackbar({
                        message:
                            error.response?.data?.detail ||
                            "Неизвестная серверная ошибка. Попробуйте заново",
                        variant: "error",
                    });
                }
            },

            isAcceptedResetpassword: false,
            sendResetEmail: async (email) => {
                try {
                    const { data } = await authApiClient.get(
                        "/api/v1/auth/reset-password?email=" + email
                    );
                    if (data.hash) {
                        set({ hash: data.hash });
                        return data.hash;
                    }
                } catch (error: any) {
                    enqueueSnackbar({
                        message:
                            error.response?.data?.detail ||
                            "Неизвестная серверная ошибка. Попробуйте заново",
                        variant: "error",
                    });
                }
                return "";
            },
        }),
        {
            name: "auth-store", // ключ в localStorage
            storage: createJSONStorage(() => localStorage),
            partialize: (state) => ({
                accessToken: state.accessToken,
                user: state.user,
                refreshToken: state.refreshToken,
                fingerPrint: state.fingerPrint,
                userAgent: state.userAgent,
                accounts: state.accounts,
                activeAccount: state.activeAccount,
            }),
        }
    )
);
